// Definizione dei ruoli
export const ROLES = {
  SUPERSAIYAN: 'supersaiyan',
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  USER: 'user',
  MANAGER: 'manager',
  MECHANIC: 'mechanic'
};

// Definizione dei permessi per componente/sezione
export const PERMISSIONS = {
  VIEW_ALL_COMPANIES: [ROLES.SUPERSAIYAN],
  DEVELOPER_OPTIONS: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN],
  MANAGE_USERS: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN],
  MANAGE_MECHANICS: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN],
  ACCESS_SHOP: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER],
  ACCESS_INVENTORY: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.USER, ROLES.MANAGER, ROLES.MECHANIC],
  ACCESS_MAINTENANCE: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.USER, ROLES.MECHANIC],
  ACCESS_CUSTOMERS: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.USER, ROLES.MECHANIC],
  ACCESS_SALES: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.USER, ROLES.MANAGER],
  ACCESS_SETTINGS: [ROLES.SUPERSAIYAN, ROLES.SUPERADMIN, ROLES.ADMIN]
};

// Helper function per verificare i permessi
export const hasPermission = (userRole, permission) => {
  if (!PERMISSIONS[permission]) return false;
  return PERMISSIONS[permission].includes(userRole);
};

// Helper function per verificare se l'utente può vedere tutte le aziende
export const canViewAllCompanies = (userRole) => {
  return hasPermission(userRole, 'VIEW_ALL_COMPANIES');
};

// Helper function per verificare se l'utente può gestire gli utenti
export const canManageUsers = (userRole) => {
  return hasPermission(userRole, 'MANAGE_USERS');
};

// Helper function per verificare se l'utente può gestire i meccanici
export const canManageMechanics = (userRole) => {
  return hasPermission(userRole, 'MANAGE_MECHANICS');
};

// Descrizione dei ruoli e dei loro permessi
export const ROLE_DESCRIPTIONS = {
  [ROLES.SUPERSAIYAN]: 'Accesso completo a tutte le funzionalità e tutte le aziende',
  [ROLES.SUPERADMIN]: 'Accesso completo a tutte le funzionalità della propria azienda',
  [ROLES.ADMIN]: 'Accesso completo eccetto Opzioni Sviluppatore e gestione Utenti',
  [ROLES.USER]: 'Accesso base eccetto Shop, Opzioni Sviluppatore e gestione Utenti/Meccanici',
  [ROLES.MANAGER]: 'Accesso limitato a Magazzino, Shop e Vendite',
  [ROLES.MECHANIC]: 'Accesso a Manutenzioni, Clienti e Magazzino'
};

export const canManageCashRegister = (user) => {
  if (!user) return false;
  return [ROLES.ADMIN, ROLES.SUPERADMIN, ROLES.SUPERSAIYAN].includes(user.role);
};

export const canViewAccountingMovements = (user) => {
  if (!user) return false;
  return hasPermission(user.role, 'ACCESS_SETTINGS');
};

/**
 * ========================================
 * Verifica l'accesso alle sezioni basandosi sui flag individuali
 * invece che solo sul ruolo dell'utente
 */

export const SECTIONS = {
  RENTALS: 'rentals',
  MAINTENANCE: 'maintenance',
  SALES: 'sales',
  SHOP: 'shop',
  ACCOUNTING: 'accounting'
};

/**
 * Verifica se l'utente ha accesso a una sezione specifica
 * @param {Object} user - Oggetto utente completo con tutti i campi
 * @param {string} section - Nome della sezione (usa SECTIONS.*)
 * @returns {boolean} - true se ha accesso, false altrimenti
 */
export const hasSectionAccess = (user, section) => {
  if (!user) return false;
  
  // Superadmin e supersaiyan hanno sempre accesso a tutto
  if (['superadmin', 'supersaiyan'].includes(user.role)) {
    return true;
  }
  
  // Verifica il permesso specifico per la sezione
  switch(section) {
    case SECTIONS.RENTALS:
      return user.accessRentals === true;
    case SECTIONS.MAINTENANCE:
      return user.accessMaintenance === true;
    case SECTIONS.SALES:
      return user.accessSales === true;
    case SECTIONS.SHOP:
      return user.accessShop === true;
    case SECTIONS.ACCOUNTING:
      return user.accessAccounting === true;
    default:
      return false;
  }
};

/**
 * Verifica se l'utente ha accesso ad almeno una delle sezioni specificate
 * @param {Object} user - Oggetto utente completo
 * @param {string[]} sections - Array di nomi di sezioni
 * @returns {boolean} - true se ha accesso ad almeno una sezione
 */
export const hasAnySectionAccess = (user, sections) => {
  if (!user || !sections || !Array.isArray(sections)) return false;
  
  // Superadmin e supersaiyan hanno sempre accesso a tutto
  if (['superadmin', 'supersaiyan'].includes(user.role)) {
    return true;
  }
  
  return sections.some(section => hasSectionAccess(user, section));
};

/**
 * Ottiene tutte le sezioni a cui l'utente ha accesso
 * @param {Object} user - Oggetto utente completo
 * @returns {string[]} - Array di nomi di sezioni accessibili
 */
export const getUserAccessibleSections = (user) => {
  if (!user) return [];
  
  // Superadmin e supersaiyan hanno accesso a tutto
  if (['superadmin', 'supersaiyan'].includes(user.role)) {
    return Object.values(SECTIONS);
  }
  
  const accessibleSections = [];
  
  if (user.accessRentals) accessibleSections.push(SECTIONS.RENTALS);
  if (user.accessMaintenance) accessibleSections.push(SECTIONS.MAINTENANCE);
  if (user.accessSales) accessibleSections.push(SECTIONS.SALES);
  if (user.accessShop) accessibleSections.push(SECTIONS.SHOP);
  if (user.accessAccounting) accessibleSections.push(SECTIONS.ACCOUNTING);
  
  return accessibleSections;
}; 